package com.partejar.app.feature.gestantes.data

enum class GestanteListStage(val apiValue: String) {
    ACTIVE("ATIVA"),
    POSTPARTUM("ARQUIVADA_POS_PARTO")
}

data class GestanteListItemData(
    val id: String,
    val name: String,
    val babyName: String?,
    val gestationalWeeks: Int?,
    val gestationalDays: Int?,
    val dueDate: String?,
    val responsible: String?,
    val stage: GestanteListStage,
    val actionRequired: String?,
    val deliveryDate: String? = null
)

data class GestantesPageData(
    val items: List<GestanteListItemData>,
    val page: Int,
    val totalElements: Long,
    val totalPages: Int,
    val isLast: Boolean
)

data class GestanteClinicalDetail(
    val id: Long,
    val name: String,
    val babyName: String?,
    val dueDate: String?,
    val gestationalWeeks: Int?,
    val gestationalDays: Int?,
    val daysUntilDueDate: Long?,
    val maternity: String?,
    val prenatalType: String?,
    val livesInBh: Boolean,
    val responsible: String?,
    val stage: GestanteListStage,
    val comorbidities: List<GestanteComorbidity>,
    val exams: List<GestanteExam> = emptyList(),
    val examTypes: List<GestanteExamType> = emptyList(),
    val delivery: GestanteDelivery? = null,
    val address: GestanteAddress = GestanteAddress()
)

data class GestanteAddress(
    val cep: String? = null, val street: String? = null, val number: String? = null,
    val complement: String? = null, val neighborhood: String? = null,
    val city: String? = null, val state: String? = null
) {
    fun display(): String? {
        val line = listOfNotNull(street?.takeIf(String::isNotBlank), number?.takeIf(String::isNotBlank),
            complement?.takeIf(String::isNotBlank)).joinToString(", ")
        return listOfNotNull(line.takeIf(String::isNotBlank), neighborhood?.takeIf(String::isNotBlank),
            listOfNotNull(city?.takeIf(String::isNotBlank), state?.takeIf(String::isNotBlank)).joinToString(" - ").takeIf(String::isNotBlank),
            cep?.takeIf(String::isNotBlank)?.let { "CEP $it" }).joinToString(" • ").ifBlank { null }
    }
}

enum class GestanteDeliveryRoute(val apiValue: String) { VAGINAL("VAGINAL"), VAGINAL_INSTRUMENTAL("VAGINAL_INSTRUMENTAL"), CESAREAN("CESARIANA"), OTHER("OUTRO") }
enum class GestanteMaternalOutcome(val apiValue: String) { WITHOUT_RECORDED_COMPLICATION("SEM_INTERCORRENCIA_REGISTRADA"), WITH_COMPLICATION("COM_INTERCORRENCIA"), OTHER("OUTRO") }
enum class GestanteNeonatalOutcome(val apiValue: String) { LIVE_BIRTH("NASCIDO_VIVO"), FETAL_NEONATAL_DEATH("OBITO_FETAL_NEONATAL"), OTHER("OUTRO") }
data class GestanteDelivery(val id: Long, val date: String, val time: String?, val route: GestanteDeliveryRoute, val location: String, val maternalOutcome: GestanteMaternalOutcome, val neonatalOutcome: GestanteNeonatalOutcome, val observation: String?, val registeredBy: String)
data class GestanteDeliveryInput(val date: String, val time: String?, val route: GestanteDeliveryRoute, val location: String, val maternalOutcome: GestanteMaternalOutcome, val neonatalOutcome: GestanteNeonatalOutcome, val observation: String?)

data class GestanteClinicalUpdate(
    val babyName: String?,
    val dueDate: String?,
    val maternity: String?,
    val prenatalType: String?,
    val livesInBh: Boolean,
    val address: GestanteAddress = GestanteAddress()
)

data class GestanteComorbidity(
    val id: Long,
    val description: String,
    val observation: String?,
    val active: Boolean
)

enum class GestanteExamStatus(val apiValue: String) {
    PENDING("PENDENTE"),
    NORMAL("NORMAL"),
    ATTENTION("ATENCAO"),
    ALTERED("ALTERADO")
}

data class GestanteExamType(
    val id: Long, val name: String, val category: String,
    val code: String? = null, val active: Boolean = true,
    val analyses: List<GestanteExamAnalysis> = emptyList()
)

data class GestanteExamAnalysis(
    val id: Long, val code: String, val name: String, val definition: String,
    val loincCode: String? = null, val unitRequired: Boolean = false,
    val evolutionEnabled: Boolean = false, val active: Boolean = true,
    val units: List<GestanteExamUnit> = emptyList()
)

data class GestanteExamUnit(
    val id: Long, val code: String, val ucumCode: String?, val label: String,
    val default: Boolean = false, val active: Boolean = true
)

data class GestanteExamResult(
    val id: Long,
    val parameter: String,
    val value: String,
    val unit: String?,
    val reference: String?,
    val status: GestanteExamStatus,
    val analysisId: Long? = null,
    val analysisUnitId: Long? = null,
    val freeResult: Boolean = false,
    val evolutionEnabled: Boolean = false
)

data class GestanteExam(
    val id: Long,
    val type: GestanteExamType,
    val performedAt: String,
    val status: GestanteExamStatus,
    val observation: String?,
    val registeredBy: String,
    val results: List<GestanteExamResult>
)

data class GestanteExamResultInput(
    val parameter: String,
    val value: String,
    val unit: String?,
    val reference: String?,
    val status: GestanteExamStatus,
    val analysisId: Long? = null,
    val analysisUnitId: Long? = null,
    val freeResult: Boolean? = null
)

data class GestanteExamInput(
    val examTypeId: Long?,
    val performedAt: String,
    val observation: String?,
    val results: List<GestanteExamResultInput>,
    val customName: String? = null
)

open class GestantesDataException(cause: Throwable? = null) : Exception(cause)
class GestantesSessionExpiredException : GestantesDataException()
class GestantesWorkspaceException : GestantesDataException()
class GestantesUnavailableException(cause: Throwable? = null) : GestantesDataException(cause)
class GestantesPayloadException : GestantesDataException()
